import { Pointer, Service } from '@yleisradio/areena-types';
import { useUILanguage } from 'hooks/useUILanguage';
import NextLink from 'next/link';
import React, { ForwardedRef, forwardRef } from 'react';
import { pointerToClientURL } from 'utils/pointer';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { serviceFromClientPath } from 'utils/url';

type PointerType = Pointer['type'];

const AREENA_POINTER_TYPES: readonly PointerType[] = [
  'clip',
  'program',
  'series',
  'app',
  'package',
  'service',
  'view',
  undefined,
];

function isInAppLink(href: string, pointer: Pointer) {
  return (
    AREENA_POINTER_TYPES.includes(pointer.type) &&
    href.startsWith('/') &&
    !href.startsWith('//')
  );
}

type LinkProps = {
  children: React.ReactNode;
  className?: string | undefined;
  download?: boolean | undefined;
  dataTestId?: string | undefined;
  id?: string | undefined;
  lang?: string | undefined;
  onClick?: React.MouseEventHandler<HTMLElement> | undefined;
  classNameCurrentPage?: string | undefined;
  pointer: Pointer;
  service?: Service | undefined;
} & React.AriaAttributes;

export const Link = forwardRef(function Link(
  {
    children,
    download,
    dataTestId,
    onClick,
    pointer,
    service: serviceOverride,
    classNameCurrentPage,
    ...restProps
  }: LinkProps,
  ref: ForwardedRef<HTMLElement>
) {
  const language = useUILanguage();
  const { asPath } = useRouter();
  const { areenaService } = useAreenaService();

  const service =
    serviceOverride ?? areenaService ?? serviceFromClientPath(asPath) ?? 'tv';

  const href = pointerToClientURL(pointer, language, service);
  const pathname = usePathname();

  if (!href) {
    return onClick ? (
      <button
        data-testid={dataTestId}
        onClick={onClick}
        type="button"
        ref={ref as ForwardedRef<HTMLButtonElement>}
        {...restProps}
      >
        {children}
      </button>
    ) : (
      <span {...restProps}>{children}</span>
    );
  }

  const className = classNames(
    restProps.className,
    pathname === href && classNameCurrentPage
  );

  if (isInAppLink(href, pointer)) {
    return (
      <NextLink
        download={download}
        data-testid={dataTestId}
        href={href}
        {...(onClick ? { onClick } : null)} // Next.js Link prop types don't accept `undefined`
        {...restProps}
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        className={className}
      >
        {children}
      </NextLink>
    );
  }

  if (AREENA_POINTER_TYPES.includes(pointer.type)) {
    return (
      <a
        download={download}
        data-testid={dataTestId}
        href={href}
        onClick={onClick}
        {...restProps}
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        className={className}
      >
        {children}
      </a>
    );
  }

  return (
    <a
      download={download}
      data-testid={dataTestId}
      href={href}
      onClick={onClick}
      rel="external"
      {...restProps}
      ref={ref as ForwardedRef<HTMLAnchorElement>}
      className={className}
    >
      {children}
    </a>
  );
});
